import { Button, Group, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect } from 'react';
import { trpc } from '../../lib/trpc';
import { SecretField } from '../config/SecretField';

/**
 * The manual / BYOC WhatsApp connect form — the expert path.
 *
 * Lifted from the existing Meta page rather than rewritten: it already gets the
 * awkward part right, which is that `accessToken` is optional on re-save. The
 * form has two modes, and Zod can't see the difference because it depends on
 * whether a number is already stored:
 *
 *   - first connect  → a key is required
 *   - editing        → leave it blank to keep the saved encrypted key
 *
 * so the requirement is enforced in `onSubmit` against `hasNumber`, and the API
 * mirrors it with `accessToken: z.string().optional()`.
 *
 * In the corridor this lives behind the third, subordinate option; in the
 * connections hub it lives inside the Advanced disclosure. Same component, so
 * the two never drift.
 */
const Schema = z.object({
  wabaId: z.string().min(1, 'Business account ID is required'),
  phoneNumberId: z.string().min(1, 'Phone number ID is required'),
  accessToken: z.string().optional(),
});
type FormValues = z.infer<typeof Schema>;

export interface ManualWhatsAppFormProps {
  onConnected: () => void;
}

export function ManualWhatsAppForm({ onConnected }: ManualWhatsAppFormProps) {
  const utils = trpc.useUtils();
  const cfg = trpc.integrations.meta.getWhatsAppManualConfig.useQuery();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: { wabaId: '', phoneNumberId: '', accessToken: '' },
  });

  // Prefill once the saved config arrives, so editing doesn't mean retyping.
  useEffect(() => {
    if (!cfg.data) return;
    reset({
      wabaId: cfg.data.wabaId ?? '',
      phoneNumberId: cfg.data.phoneNumberId ?? '',
      accessToken: '',
    });
  }, [cfg.data, reset]);

  const connect = trpc.integrations.meta.connectWhatsAppManual.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'WhatsApp connected', color: 'green' });
      void utils.integrations.meta.status.invalidate();
      void utils.integrations.meta.getWhatsAppManualConfig.invalidate();
      onConnected();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const isUpdate = !!cfg.data?.hasNumber;
  const accessToken = watch('accessToken') ?? '';

  const onSubmit = handleSubmit((values) => {
    const token = values.accessToken?.trim() ?? '';
    if (!isUpdate && !token) {
      notifications.show({ message: 'A connection key is required to connect', color: 'red' });
      return;
    }
    connect.mutate({
      wabaId: values.wabaId.trim(),
      phoneNumberId: values.phoneNumberId.trim(),
      ...(token ? { accessToken: token } : {}),
    });
  });

  return (
    <form onSubmit={onSubmit}>
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          You&apos;ll find these in your Meta business settings, under your WhatsApp Business
          account.
        </Text>
        <TextInput
          label="Business account ID"
          placeholder="e.g. 102938475610293"
          {...register('wabaId')}
          error={errors.wabaId?.message}
        />
        <TextInput
          label="Phone number ID"
          placeholder="e.g. 109283746518273"
          {...register('phoneNumberId')}
          error={errors.phoneNumberId?.message}
        />
        <SecretField
          label="Connection key"
          description={
            isUpdate
              ? 'Leave blank to keep the existing key. Paste a new one to replace it.'
              : 'A permanent access token from your Meta business settings. Stored encrypted.'
          }
          present={!!cfg.data?.hasAccessToken}
          value={accessToken}
          onChange={(v) => setValue('accessToken', v, { shouldDirty: true })}
          name="accessToken"
          error={errors.accessToken?.message}
          onReveal={() =>
            utils.integrations.meta.revealSecret
              .fetch({ kind: 'whatsapp_token' })
              .then((r) => r.value)
          }
        />
        <Group justify="flex-end">
          <Button type="submit" loading={connect.isPending}>
            {isUpdate ? 'Update connection' : 'Connect WhatsApp'}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
